import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../__generated__/gql";
import { useCallback, useMemo } from "react";
import EditableTable from "../components/EditableTable/EditableTable";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import EditableCell from "../components/EditableCell/EditableCell";
import OptionsCell from "../components/OptionsCell/OptionsCell";
import { FormProvider, useForm } from "react-hook-form";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import Loader from "../../../../ui/Loader/Loader";
import { useToast } from "../../../../ui/Toast/ToastContext";

const GoalPagesQuery = graphql(/* GraphQL */ `
  query goalPages {
    goalPages {
      id
      name
      url
      positioning
    }
  }
`);

const CreateGoalPagesMutation = graphql(/* GraphQL */ `
  mutation createGoalPages($input: [CreateGoalPageInput!]!) {
    createGoalPages(input: $input) {
      id
      name
      url
      positioning
    }
  }
`);

const UpdateGoalPagesMutation = graphql(/* GraphQL */ `
  mutation updateGoalPages($input: [UpdateGoalPageInput!]!) {
    updateGoalPages(input: $input) {
      id
      name
      url
      positioning
    }
  }
`);

const DeleteGoalPagesMutation = graphql(/* GraphQL */ `
  mutation deleteGoalPages($ids: [String!]!) {
    deleteGoalPages(ids: $ids) {
      id
    }
  }
`);

type GoalPagesTableRow = {
  id?: string;
  name?: string | null;
  url?: string | null;
  positioning?: string | null;
};

type GoalPagesTableFormData = {
  rows: GoalPagesTableRow[];
};

const columnHelper = createColumnHelper<GoalPagesTableRow>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<GoalPagesTableRow, any>[] = [
  columnHelper.accessor("name", {
    header: "Page Name",
    size: 300,
    cell: EditableCell,
    meta: {
      tooltip: "The name of the page or goal on the page",
    },
  }),
  columnHelper.accessor("url", {
    header: "URL",
    minSize: 300,
    cell: EditableCell,
    meta: {
      tooltip: "The URL of the page",
    },
  }),
  columnHelper.accessor("positioning", {
    header: "Positioning",
    minSize: 300,
    cell: EditableCell,
    meta: {
      tooltip:
        "How you would like Macaw to position the goal when writing call to actions",
    },
  }),
  columnHelper.display({
    id: "Options",
    size: 72,
    meta: {
      sticky: true,
    },
    cell: OptionsCell,
  }),
];

function GoalPagesTable({ data }: { data: GoalPagesTableRow[] }) {
  const [, updateGoalPages] = useMutation(UpdateGoalPagesMutation);
  const [, createGoalPages] = useMutation(CreateGoalPagesMutation);
  const [, deleteGoalPages] = useMutation(DeleteGoalPagesMutation);
  const { showToast } = useToast();

  const methods = useForm<GoalPagesTableFormData>({
    defaultValues: { rows: data },
  });

  const onSubmit = useCallback(
    async (data: GoalPagesTableFormData) => {
      const rowsWithIndex = data.rows.map((row, index) => ({ index, ...row }));
      const goalPagesToCreate = rowsWithIndex.filter((f) => !f.id);
      const goalPagesToUpdate = rowsWithIndex.filter(
        (f, index): f is typeof f & { id: string } =>
          !!(f.id && methods.getFieldState(`rows.${index}`).isDirty),
      );
      const goalPagesToDelete =
        methods.formState.defaultValues?.rows?.filter(
          (f): f is typeof f & { id: string } =>
            !!(f?.id && !data.rows.find((row) => row.id === f.id)),
        ) ?? [];
      const [createdGoalPages, updatedGoalPages] = await Promise.all([
        goalPagesToCreate.length
          ? createGoalPages({
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              input: goalPagesToCreate.map(({ index, id, ...f }) => f),
            })
          : null,
        goalPagesToUpdate.length
          ? updateGoalPages({
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              input: goalPagesToUpdate.map(({ index, ...f }) => f),
            })
          : null,
        goalPagesToDelete.length
          ? deleteGoalPages({
              ids: goalPagesToDelete.map((f) => f?.id),
            })
          : null,
      ]);

      const newGoalPages = data.rows.map((goalPage, index) => {
        const createdGoalPageIndex = goalPagesToCreate.findIndex(
          (f) => f.index === index,
        );

        if (createdGoalPageIndex >= 0) {
          return createdGoalPages?.data?.createGoalPages[createdGoalPageIndex];
        }

        const updatedGoalPageIndex = goalPagesToUpdate.findIndex(
          (f) => f.index === index,
        );

        if (updatedGoalPageIndex >= 0) {
          return updatedGoalPages?.data?.updateGoalPages[updatedGoalPageIndex];
        }

        return goalPage;
      });

      methods.reset({ rows: newGoalPages.filter(Boolean) });
      showToast({
        title: "Goal pages saved",
        variant: "success",
      });
    },
    [createGoalPages, deleteGoalPages, methods, showToast, updateGoalPages],
  );

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal isDirty={methods.formState.isDirty} />
      <EditableTable
        propertyName="rows"
        onSubmit={onSubmit}
        columns={columns}
        title="Goal Pages"
        description="Choose where you want to drive readers to convert. Macaw will automatically select the most relevant Goal Page. Less is more, if you only have one conversion page—that’s ideal!"
        resourceName={{ plural: "goal pages", singular: "goal page" }}
        maxRows={45}
        defaultValue={{
          id: "",
          name: "",
          url: "",
          positioning: "",
        }}
      />
    </FormProvider>
  );
}

function GoalPages() {
  const [goalPagesResponse] = useQuery({ query: GoalPagesQuery });

  const data: GoalPagesTableRow[] = useMemo(() => {
    if (!goalPagesResponse.data?.goalPages?.length) {
      return [];
    }

    return goalPagesResponse.data.goalPages.map((goalPage) => ({
      id: goalPage.id,
      name: goalPage.name,
      url: goalPage.url,
      positioning: goalPage.positioning,
    }));
  }, [goalPagesResponse]);

  if (goalPagesResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return <GoalPagesTable data={data} />;
}

export default GoalPages;
